import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTarget } from "lib/@hooks/use-target"
import { Box, Button, Card, CardActions, CardContent, CardHeader, Stack, Typography } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh, noChange } from "lib/@hooks/useRefresh"
import noop from "lib/noop"
import { MappingConfidenceThreshold } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/setting-components/mapping-confidence-threshold.js"
import { MappingSettingsLegislationContent } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/setting-components/mapping-settings-legislation-content.js"
import { MappingSettingsFacilityType } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/setting-components/mapping-settings-facility-type.js"
import { MappingAssetTypeSetting } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/setting-components/mapping-asset-type-setting.js"
import { MappingScheduleTypePreference } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/setting-components/mapping-schedule-type-preference.js"

export function InnerSettings() {
    const {
        cancel = noop,
        ok = noop,
        saveSettings = noop,
        setHideSettings = noop,
        originalOptions,
        haveSettingsChanged = alwaysChanged,
    } = useBoundContext()
    const target = useTarget()
    const refresh = useRefresh(noChange)
    const hasChanges = haveSettingsChanged(originalOptions?.current, target)

    return (
        <Box data-cy="mapping-settings">
            <Bound onChange={onSettingChange}>
                <Card>
                    <CardHeader title={"Mapping Settings"} />
                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Stack>
                            <Stack pb={1}>
                                <Typography variant="subtitle1" py={1}>
                                    Asset Type Settings
                                </Typography>
                                <MappingAssetTypeSetting />
                            </Stack>
                            <Typography variant="subtitle1" py={1}>
                                Accuracy
                            </Typography>
                            <MappingConfidenceThreshold />
                        </Stack>
                        <Stack>
                            <Typography variant="subtitle1" py={1}>
                                Schedule Library Preference*
                            </Typography>
                            <MappingScheduleTypePreference />
                        </Stack>
                        <Stack>
                            <Typography variant="subtitle1" py={1}>
                                Legislation Settings
                            </Typography>
                            <MappingSettingsLegislationContent />
                        </Stack>
                        <Stack pb={1}>
                            <Typography variant="subtitle1" py={1}>
                                Facility Type Settings
                            </Typography>
                            <MappingSettingsFacilityType />
                        </Stack>
                    </CardContent>
                    <CardActions>
                        <ListItemBox spacing={1}>
                            <Box flex={1} />
                            <Button
                                onClick={cancel}
                                color="secondary"
                                data-cy="asset-mapping-schedule-mapping-cancel-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={configureSettings}
                                variant="contained"
                                disabled={!hasChanges}
                                data-cy="asset-mapping-schedule-mapping-apply-settings-button"
                            >
                                Apply Settings
                            </Button>
                        </ListItemBox>
                    </CardActions>
                </Card>
            </Bound>
        </Box>
    )

    async function configureSettings() {
        // FIQ-1554: Apply can be clicked inside the EditRecord's 1s save debounce. Stamp
        // the flag on the live edit target so the pending debounced write-back cannot
        // revert it, and hand the current edits straight to save so they are never lost
        // to that race (previously the fallback saved the register's original options).
        target.hasConfiguredSettings = true
        const saved = await saveSettings(target)
        if (!saved) return
        setHideSettings(true)
        ok()
    }

    function onSettingChange() {
        refresh()
    }

    function alwaysChanged() {
        return true
    }
}
